import { Component, computed, inject, signal } from '@angular/core';
import { PhotoUpload, PhotoUploadService } from './photo-upload.service';

@Component({
  selector: 'app-photo-upload-indicator',
  host: { '[class.expanded]': 'expanded()' },
  template: `
    @if (queue.uploads().length || queue.storageError()) {
      <aside class="upload-panel" aria-label="Photo uploads">
        <button type="button" class="summary" (click)="expanded.set(!expanded())" [attr.aria-expanded]="expanded()" [attr.aria-label]="summary() + (expanded() ? '. Minimize photo uploads' : '. Show photo uploads')" [attr.aria-controls]="expanded() ? 'photo-upload-details' : null">
          @if (expanded()) {
            <span>{{ summary() }}</span><span aria-hidden="true">-</span>
          } @else {
            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 16V3m-5 5 5-5 5 5M4 15v5a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-5" /></svg>
            <span aria-hidden="true">{{ compactCount() }}</span>
          }
        </button>
        <span class="screen-reader-status" aria-live="polite">{{ summary() }}</span>
        @if (expanded()) {
          <div id="photo-upload-details" class="details">
            <p class="hint">Pending photos are saved on this device. Reopen the app and sign in with the same account to resume. Keep it open for uploads to finish sooner.</p>
            @if (queue.storageError()) {
              <p role="alert">{{ queue.storageError() }}</p>
              <button type="button" class="clear" (click)="queue.retryStorage()">Retry device storage</button>
            }
            @if (queue.otherTab()) { <p role="status">Another app tab is managing uploads. If it was closed, recovery can take up to two minutes.</p> }
            @if (queue.paused()) { <p role="status">Uploads paused. {{ queue.online() ? 'Sign in with the same account to resume.' : 'Waiting for an internet connection.' }}</p> }
            <ul>
              @for (item of queue.uploads(); track item.key) {
                <li>
                  <p class="destination">{{ item.modelType }} {{ item.referenceId }}</p>
                  <p class="filename">{{ item.name }}</p>
                  <p class="status">{{ label(item) }}</p>
                  @if (item.state === 'uploading') {
                    <progress [value]="item.progress" max="100" [attr.aria-label]="'Upload progress for ' + item.name"></progress>
                  }
                  @if (item.message && item.state !== 'saved') { <p class="hint">{{ item.message }}</p> }
                  <div class="actions">
                    @if (item.state === 'failed' || item.state === 'waiting') {
                      <button type="button" (click)="queue.retry(item.key)" [disabled]="queue.paused()">Retry</button>
                    }
                    @if (item.state !== 'saved' && item.state !== 'cancelled') {
                      <button type="button" (click)="queue.cancel(item.key)">{{ item.state === 'uploading' ? 'Stop upload' : 'Remove' }}</button>
                    }
                  </div>
                </li>
              }
            </ul>
            <button type="button" class="clear" (click)="queue.dismissFinished()">Clear finished items</button>
          </div>
        }
      </aside>
    }
  `,
  styles: `
    :host { position: fixed; z-index: 60; right: 0; top: 45%; transform: translateY(-50%); width: 64px; pointer-events: none; }
    :host(.expanded) { right: 12px; top: auto; bottom: calc(104px + env(safe-area-inset-bottom)); transform: none; width: min(340px, calc(100vw - 24px)); }
    .upload-panel { pointer-events: auto; }
    :host(:not(.expanded)) .upload-panel { border-radius: 12px 0 0 12px; }
    :host(:not(.expanded)) .summary { min-height: 56px; padding: 8px 6px; flex-direction: column; align-items: center; justify-content: center; gap: 3px; font-size: 11px; }
    .screen-reader-status { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
    .upload-panel { background: white; color: #163247; border: 1px solid #cbd5e1; border-radius: 14px; box-shadow: 0 8px 24px #0f172a26; overflow: hidden; font-size: 13px; }
    button { cursor: pointer; min-height: 44px; font: inherit; }
    button:focus-visible { outline: 3px solid #0284c7; outline-offset: -3px; }
    button:disabled { opacity: .5; cursor: default; }
    .summary { width: 100%; background: #0f334a; color: white; padding: 10px 14px; display: flex; justify-content: space-between; gap: 12px; text-align: left; font-weight: 600; }
    .details { max-height: min(55dvh, 420px, calc(100dvh - 220px - env(safe-area-inset-bottom))); overflow: auto; padding: 12px; }
    ul { list-style: none; padding: 0; margin: 0; }
    li { padding: 10px 0; border-bottom: 1px solid #e2e8f0; }
    p { margin: 0 0 4px; overflow-wrap: anywhere; }
    .destination { font-weight: 700; } .filename, .hint { color: #475569; font-size: 12px; }
    .status { font-weight: 600; } progress { width: 100%; accent-color: #1a6fa6; }
    .actions { display: flex; gap: 8px; } .actions button, .clear { padding: 6px 12px; color: #0f334a; text-decoration: underline; }
  `
})
export class PhotoUploadIndicatorComponent {
  readonly queue = inject(PhotoUploadService);
  readonly expanded = signal(false);
  readonly compactCount = computed(() => {
    if (this.queue.storageError()) return '!';
    const items = this.queue.uploads().filter(item => item.state !== 'cancelled');
    return `${items.filter(item => item.state === 'saved').length}/${items.length}`;
  });
  readonly summary = computed(() => {
    if (this.queue.storageError()) return 'Photo storage needs attention';
    const items = this.queue.uploads();
    const saved = items.filter(item => item.state === 'saved').length;
    const failed = items.filter(item => item.state === 'failed').length;
    const working = items.some(item => ['queued', 'preparing', 'uploading', 'waiting'].includes(item.state));
    const total = items.filter(item => item.state !== 'cancelled').length;
    return `${saved} of ${total} photos saved${working ? (this.queue.paused() ? ' - Paused' : ' - Uploading') : ''}${failed ? ` - ${failed} failed` : ''}`;
  });
  label(item: PhotoUpload) {
    switch (item.state) {
      case 'preparing': return 'Preparing photo';
      case 'uploading': return item.progress === 100 ? 'Saving on server...' : `Uploading ${item.progress}%`;
      case 'waiting': return 'Waiting to retry';
      case 'failed': return 'Not confirmed saved';
      case 'cancelled': return 'Stopped';
      case 'saved': return 'Saved';
      default: return 'Queued';
    }
  }
}
